import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ownerCampaignRecipients, ownerCampaignTest, ownerCampaignSend, ownerCampaignSendSlugs } from "../lib/api";

const BATCH = 40;

export default function AdminOwnerCampaign() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await ownerCampaignRecipients();
      setData(d);
      setSelected(new Set());
    } catch {
      toast.error("could not load recipients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pendingList = data?.pending_list || data?.sample || [];

  const toggle = (slug) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });

  const selectAll = () => setSelected(new Set(pendingList.map((s) => s.slug)));
  const clearAll = () => setSelected(new Set());

  const sendTest = async () => {
    const email = testEmail.trim();
    if (!email) return toast.error("enter an email to test");
    setTesting(true);
    try {
      await ownerCampaignTest(email);
      toast.success(`test sent to ${email}`);
    } catch {
      toast.error("test send failed");
    } finally {
      setTesting(false);
    }
  };

  const sendSelected = async () => {
    const slugs = [...selected];
    if (!slugs.length) return toast.error("tick at least one owner");
    if (!window.confirm(`Send the claim-invite email to ${slugs.length} selected owner(s), from contact@terminalvalue.io? This sends real email.`)) return;
    setSending(true);
    try {
      const r = await ownerCampaignSendSlugs(slugs);
      setProgress({ sent: r.sent || 0, errors: r.errors || 0, remaining: r.remaining });
      toast.success(`sent ${r.sent || 0}${r.errors ? `, ${r.errors} failed` : ""}`);
    } catch {
      toast.error("send failed — some may not have gone out");
    } finally {
      setSending(false);
      load();
    }
  };

  const sendAll = async () => {
    const pending = data?.pending || 0;
    if (!pending) return;
    if (!window.confirm(`Send the claim-invite email to ALL ${pending} pending owner(s), from contact@terminalvalue.io? This sends real email.`)) return;
    setSending(true);
    let totalSent = 0;
    let totalErr = 0;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const r = await ownerCampaignSend(BATCH);
        totalSent += r.sent || 0;
        totalErr += r.errors || 0;
        setProgress({ sent: totalSent, errors: totalErr, remaining: r.remaining });
        if (!r.remaining || r.remaining <= 0) break;
      }
      toast.success(`done — ${totalSent} sent${totalErr ? `, ${totalErr} failed` : ""}`);
    } catch {
      toast.error("send stopped — some may not have gone out");
    } finally {
      setSending(false);
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-owner-campaign">
      <div>
        <h2 className="text-[15px] font-bold">owner outreach — claim-your-listing invite</h2>
        <p className="text-[12px] text-[#666] mt-1 max-w-[70ch]">
          Emails an approved tool&rsquo;s submitter (one email per tool), from <b>contact@terminalvalue.io</b>, inviting
          them to claim their page, add a badge and advertise. Copy adapts: unclaimed owners get a claim nudge,
          claimed owners get the advertise/upgrade pitch. Everyone is emailed only once — anyone already sent is skipped.
          <b> Tick specific owners to send to just them</b> (handy for testing copy on a small group), or send to all pending.
        </p>
      </div>

      {loading ? (
        <div className="text-[13px] text-[#888]" data-testid="campaign-loading">loading…</div>
      ) : data ? (
        <>
          <div className="flex flex-wrap gap-3">
            <Stat label="owners total" value={data.total} testid="campaign-total" />
            <Stat label="already sent" value={data.sent} testid="campaign-sent" />
            <Stat label="pending" value={data.pending} testid="campaign-pending" highlight />
            <Stat label="selected" value={selected.size} testid="campaign-selected" />
          </div>

          <div className="border border-black p-4 space-y-3">
            <div className="text-[12px] font-bold">1 · send yourself a test first</div>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@yourdomain.com"
                className="border border-black px-2 py-1 text-[13px] w-[260px] max-w-full"
                data-testid="campaign-test-email"
              />
              <button
                onClick={sendTest}
                disabled={testing}
                className="border border-black bg-black text-white px-3 py-1 text-[12px] disabled:opacity-50"
                data-testid="campaign-test-send"
              >
                {testing ? "sending…" : "send test"}
              </button>
            </div>
          </div>

          <div className="border border-black p-4 space-y-3">
            <div className="text-[12px] font-bold">2 · choose who to send to</div>
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={selectAll} className="border border-black px-3 py-1 text-[12px]" data-testid="campaign-select-all">
                select all ({pendingList.length})
              </button>
              <button onClick={clearAll} className="border border-black px-3 py-1 text-[12px]" data-testid="campaign-clear">
                clear
              </button>
              <button
                onClick={sendSelected}
                disabled={sending || !selected.size}
                className="border border-black bg-[#ffff00] px-3 py-2 text-[13px] font-bold disabled:opacity-50"
                data-testid="campaign-send-selected"
              >
                {sending ? "sending…" : `send to selected (${selected.size}) →`}
              </button>
              <span className="text-[#ccc]">|</span>
              <button
                onClick={sendAll}
                disabled={sending || !data.pending}
                className="border border-black px-3 py-2 text-[13px] disabled:opacity-50"
                data-testid="campaign-send-all"
              >
                {sending ? "sending…" : `send to all pending (${data.pending}) →`}
              </button>
            </div>
            {progress && (
              <div className="text-[12px] text-[#444]" data-testid="campaign-progress">
                sent {progress.sent}{progress.errors ? ` · ${progress.errors} failed` : ""} · {progress.remaining} still pending
              </div>
            )}
          </div>

          <div>
            <div className="text-[12px] font-bold mb-2">pending recipients ({pendingList.length})</div>
            <div className="border border-black divide-y divide-black text-[12px]" data-testid="campaign-list">
              {pendingList.length === 0 ? (
                <div className="p-2 text-[#888]" data-testid="campaign-list-empty">none pending — everyone has been emailed.</div>
              ) : (
                pendingList.map((s) => (
                  <label
                    key={s.slug}
                    className={`flex flex-wrap items-center gap-3 p-2 cursor-pointer ${selected.has(s.slug) ? "bg-[#fffbcc]" : ""}`}
                    data-testid={`campaign-row-${s.slug}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(s.slug)}
                      onChange={() => toggle(s.slug)}
                      className="accent-black"
                      data-testid={`campaign-check-${s.slug}`}
                    />
                    <span className="font-mono font-bold flex-1 min-w-[160px]">{s.title}</span>
                    <span className="text-[#666] flex-1 min-w-[200px]">{s.submitter_email}</span>
                    <span className={s.claimed ? "text-green-700" : "text-[#999]"}>{s.claimed ? "claimed" : "unclaimed"}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="text-[13px] text-[#888]">could not load.</div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div className={`border border-black px-4 py-2 ${highlight ? "bg-[#ffff00]" : ""}`} data-testid={testid}>
      <div className="text-[20px] font-bold leading-none">{value}</div>
      <div className="text-[11px] text-[#666] uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}
